/**
 * Design Options — compact bar shown ABOVE the 2D SchematicBuilder.
 *
 * Product name: **Design Option** (internal collection: `scene_versions`).
 * Kept as a horizontal chip strip that wraps on narrow viewports. Never
 * introduces a fourth permanent column — canvas hard-widths preserved.
 */
import { useState } from "react";
import { Save, Layers3, GitCompare, Eye, RotateCcw, Copy, Archive, MoreHorizontal } from "lucide-react";

export default function DesignOptionsBar({
  options = [],
  activeOptionId = null,
  onSaveOption,
  onOpenManager,
  onOpenCompare,
  onSelectOption,
  onExitReadOnly,
}) {
  return (
    <div className="border border-border bg-card px-3 py-2 flex items-center flex-wrap gap-2"
         data-testid="design-options-bar">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">Design Options</div>

      <button type="button" data-testid="chip-working-design"
              aria-selected={!activeOptionId}
              onClick={onExitReadOnly}
              className={`text-[11px] min-h-[32px] px-3 border transition-colors inline-flex items-center gap-2 ${!activeOptionId ? "border-foreground bg-foreground text-background" : "border-border text-foreground hover:border-foreground"}`}>
        <span className={`w-2 h-2 rounded-full ${!activeOptionId ? "bg-emerald-400" : "bg-emerald-500/50"}`}/>
        Current Design
      </button>

      {options.length > 0 && (
        <div className="flex items-center flex-wrap gap-1 min-w-0">
          {options.slice(0, 4).map((o) => (
            <OptionChip key={o.id} option={o} active={o.id === activeOptionId} onClick={() => onSelectOption(o.id)}/>
          ))}
          {options.length > 4 && (
            <span className="text-[10px] text-muted-foreground px-1"
                  data-testid="chip-overflow">+{options.length - 4}</span>
          )}
        </div>
      )}

      <div className="flex-1"/>

      <button type="button" data-testid="btn-save-option"
              onClick={onSaveOption}
              className="btn-outline text-[11px] min-h-[32px] inline-flex items-center gap-1">
        <Save className="w-3 h-3" strokeWidth={1.5}/> Save Option
      </button>
      {options.length >= 2 && (
        <button type="button" data-testid="btn-compare-options"
                onClick={onOpenCompare}
                className="btn-outline text-[11px] min-h-[32px] inline-flex items-center gap-1">
          <GitCompare className="w-3 h-3" strokeWidth={1.5}/> Compare
        </button>
      )}
      <button type="button" data-testid="btn-manage-options"
              onClick={onOpenManager}
              className="btn-outline text-[11px] min-h-[32px] inline-flex items-center gap-1">
        <Layers3 className="w-3 h-3" strokeWidth={1.5}/> Manage
      </button>
    </div>
  );
}

function OptionChip({ option, active, onClick }) {
  const statusColour = {
    saved: "bg-slate-400",
    client_option: "bg-blue-500",
    approved: "bg-emerald-500",
    archived: "bg-neutral-400",
  }[option.status] || "bg-slate-400";
  return (
    <button type="button"
            data-testid={`chip-option-${option.id}`}
            aria-selected={active}
            onClick={onClick}
            title={`V${option.version_number} — ${option.name} · ${option.status}`}
            className={`text-[11px] min-h-[32px] px-3 border inline-flex items-center gap-2 max-w-[220px] transition-colors ${active ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"}`}>
      <span className={`w-2 h-2 rounded-full shrink-0 ${statusColour}`}/>
      <span className="truncate">V{option.version_number} · {option.name}</span>
    </button>
  );
}


/**
 * Read-only viewing banner — sits directly below the DesignOptionsBar
 * when the user is inspecting a saved Design Option.
 */
export function ReadOnlyBanner({ option, onExit }) {
  return (
    <div className="border border-amber-500 bg-amber-50 text-amber-900 px-3 py-2 flex items-center gap-2 text-[11px]"
         data-testid="read-only-banner">
      <Eye className="w-3 h-3" strokeWidth={1.5}/>
      <span className="font-medium">Viewing Saved Design Option — Read Only</span>
      <span className="text-amber-800/70 truncate">V{option.version_number} · {option.name} · {option.status}</span>
      <div className="flex-1"/>
      <button type="button" data-testid="btn-exit-readonly" onClick={onExit}
              className="text-[11px] min-h-[28px] px-2 border border-amber-700 hover:bg-amber-100">
        Exit to Current Design
      </button>
    </div>
  );
}


/**
 * Save Option modal — collects Name / Description / Status and posts to
 * `POST /projects/{pid}/scene-versions`.
 */
export function SaveOptionModal({ suggestedName = "", customScope = [], onSave, onClose }) {
  const [name, setName] = useState(suggestedName);
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("saved");
  const [selectedIds, setSelectedIds] = useState(() => new Set(customScope.map((c) => c.id)));
  const [busy, setBusy] = useState(false);
  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onSave({
        name: name.trim(), description: description.trim(), status,
        custom_scope_ids: customScope.length ? Array.from(selectedIds) : undefined,
      });
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="save-option-modal"
         className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-card border border-border p-6 max-w-md w-full space-y-4"
           onClick={(e) => e.stopPropagation()}>
        <div>
          <div className="text-sm font-medium">Save Design Option</div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Save the current design as a Design Option — a frozen snapshot you can present, compare or restore later.
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="overline text-[10px] block mb-1">Name</label>
            <input data-testid="input-option-name" className="luxe-input"
                   value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Option A — Travertine"/>
          </div>
          <div>
            <label className="overline text-[10px] block mb-1">Description</label>
            <textarea data-testid="input-option-description" className="luxe-input" rows={3}
                      value={description} onChange={(e) => setDescription(e.target.value)}
                      placeholder="Floating cabinetry with warm stone finish…"/>
          </div>
          <div>
            <label className="overline text-[10px] block mb-1">Status</label>
            <select data-testid="input-option-status" className="luxe-input"
                    value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="saved">Saved</option>
              <option value="client_option">Client Option</option>
              <option value="approved">Approved</option>
            </select>
          </div>
          {customScope.length > 0 && (
            <div data-testid="scope-picker">
              <div className="flex items-center justify-between mb-1">
                <label className="overline text-[10px]">Custom Scope Included</label>
                <div className="flex gap-2 text-[10px]">
                  <button type="button" data-testid="scope-select-all"
                          onClick={() => setSelectedIds(new Set(customScope.map((c) => c.id)))}
                          className="text-muted-foreground hover:text-foreground">Select all</button>
                  <button type="button" data-testid="scope-clear-all"
                          onClick={() => setSelectedIds(new Set())}
                          className="text-muted-foreground hover:text-foreground">Clear</button>
                </div>
              </div>
              <ul className="border border-border max-h-40 overflow-y-auto divide-y divide-border">
                {customScope.map((c) => (
                  <li key={c.id} className="flex items-center gap-2 px-2 py-1.5 text-[11px]">
                    <input type="checkbox" data-testid={`scope-check-${c.id}`}
                            checked={selectedIds.has(c.id)}
                            onChange={() => toggle(c.id)}/>
                    <span className="truncate">{c.description || c.id}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" data-testid="btn-cancel-save-option"
                  onClick={onClose}
                  className="btn-outline text-xs min-h-[36px] px-3">Cancel</button>
          <button type="button" data-testid="btn-confirm-save-option"
                  disabled={busy}
                  onClick={submit}
                  className="btn-accent text-xs min-h-[36px] px-3 disabled:opacity-40">
            {busy ? "Saving…" : "Save Option"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Options Manager — drawer/dialog listing every Design Option with
 * per-row actions.
 */
export function OptionsManagerDrawer({ options, onClose, onView, onRestore, onDuplicate, onRename, onChangeStatus, onArchive, onUnarchive }) {
  return (
    <div data-testid="options-manager"
         className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end"
         onClick={onClose}>
      <div className="bg-card border-l border-border max-w-2xl w-full h-full overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-card border-b border-border px-4 py-3 flex items-center justify-between z-10">
          <div>
            <div className="text-sm font-medium">Manage Design Options</div>
            <div className="text-[11px] text-muted-foreground">{options.length} saved · Current Design remains editable</div>
          </div>
          <button type="button" data-testid="btn-close-manager" onClick={onClose}
                  className="btn-outline text-[11px] min-h-[32px] px-3">Close</button>
        </div>
        {options.length === 0 && (
          <div className="p-8 text-center text-xs text-muted-foreground" data-testid="manager-empty">
          <div className="p-6 text-xs text-muted-foreground" data-testid="design-options-empty-state">
            <div className="font-serif text-lg text-foreground/80 mb-1">No saved designs yet</div>
            <div>Save the current design when you want to compare options or present alternatives to your client.</div>
          </div>
          </div>
        )}
        <ul className="divide-y divide-border">
          {options.map((o) => (
            <ManagerRow key={o.id} option={o}
                        onView={onView} onRestore={onRestore} onDuplicate={onDuplicate}
                        onRename={onRename} onChangeStatus={onChangeStatus}
                        onArchive={onArchive} onUnarchive={onUnarchive}/>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ManagerRow({ option, onView, onRestore, onDuplicate, onRename, onChangeStatus, onArchive, onUnarchive }) {
  const [menu, setMenu] = useState(false);
  const isArchived = option.status === "archived";
  return (
    <li className="px-4 py-3 flex items-start gap-3" data-testid={`manager-row-${option.id}`}>
      <div className="w-24 shrink-0 border border-border bg-white overflow-hidden">
        {option.thumbnail_svg ? (
          <div dangerouslySetInnerHTML={{ __html: option.thumbnail_svg }}
               style={{ width: 96, height: 64 }}
               data-testid={`manager-thumb-${option.id}`}/>
        ) : (
          <div className="w-24 h-16 bg-muted"/>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">
          V{option.version_number} · {option.name}
          <StatusPill status={option.status}/>
        </div>
        <div className="text-[10px] text-muted-foreground truncate">
          Created: {(option.created_at || "").slice(0, 10)}
          {option.parent_version_id ? " · Parent linked" : ""}
        </div>
        {option.description && (
          <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{option.description}</div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button type="button" data-testid={`btn-view-${option.id}`}
                onClick={() => onView(option)}
                className="btn-outline text-[11px] min-h-[32px] px-2 inline-flex items-center gap-1">
          <Eye className="w-3 h-3" strokeWidth={1.5}/> View
        </button>
        <button type="button" data-testid={`btn-restore-${option.id}`}
                onClick={() => onRestore(option)}
                className="btn-outline text-[11px] min-h-[32px] px-2 inline-flex items-center gap-1">
          <RotateCcw className="w-3 h-3" strokeWidth={1.5}/> Restore
        </button>
        <button type="button" data-testid={`btn-duplicate-${option.id}`}
                onClick={() => onDuplicate(option)}
                className="btn-outline text-[11px] min-h-[32px] px-2 inline-flex items-center gap-1">
          <Copy className="w-3 h-3" strokeWidth={1.5}/> Duplicate
        </button>
        <div className="relative">
          <button type="button" data-testid={`btn-more-${option.id}`}
                  onClick={() => setMenu((v) => !v)}
                  className="btn-outline text-[11px] min-h-[32px] px-2">
            <MoreHorizontal className="w-3 h-3" strokeWidth={1.5}/>
          </button>
          {menu && (
            <div className="absolute right-0 top-full mt-1 bg-card border border-border shadow-xl z-20 min-w-[180px]"
                 data-testid={`menu-${option.id}`}
                 onClick={(e) => e.stopPropagation()}>
              <MenuItem testid={`menu-rename-${option.id}`} onClick={() => { setMenu(false); onRename(option); }}>Rename</MenuItem>
              <MenuItem testid={`menu-status-${option.id}`} onClick={() => { setMenu(false); onChangeStatus(option); }}>Change status…</MenuItem>
              {!isArchived
                ? <MenuItem testid={`menu-archive-${option.id}`} onClick={() => { setMenu(false); onArchive(option); }}>
                    <Archive className="w-3 h-3" strokeWidth={1.5}/> Archive
                  </MenuItem>
                : <MenuItem testid={`menu-unarchive-${option.id}`} onClick={() => { setMenu(false); onUnarchive(option); }}>
                    Unarchive
                  </MenuItem>}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function MenuItem({ testid, onClick, children }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
            className="w-full text-left text-[11px] px-3 py-2 hover:bg-muted inline-flex items-center gap-2">
      {children}
    </button>
  );
}

function StatusPill({ status }) {
  const label = {
    saved: "SAVED",
    client_option: "CLIENT OPTION",
    approved: "APPROVED",
    archived: "ARCHIVED",
  }[status] || "SAVED";
  const cls = {
    saved: "border-slate-400 text-slate-700",
    client_option: "border-blue-500 text-blue-700",
    approved: "border-emerald-500 text-emerald-700",
    archived: "border-neutral-400 text-neutral-500",
  }[status] || "border-slate-400 text-slate-700";
  return (
    <span className={`ml-2 inline-block text-[9px] uppercase tracking-widest border px-1.5 py-[1px] ${cls}`}
          data-testid={`status-pill-${status}`}>{label}</span>
  );
}


/**
 * Compare Options modal — selects two options + shows thumbnails +
 * headline metadata side-by-side. Future work will add 2D | 3D | Render
 * tabs per side.
 */
export function CompareOptionsModal({ options, onClose }) {
  const active = options.filter((o) => o.status !== "archived");
  const [aId, setAId] = useState(active[0]?.id || "");
  const [bId, setBId] = useState(active[1]?.id || active[0]?.id || "");
  const a = options.find((o) => o.id === aId);
  const b = options.find((o) => o.id === bId);
  return (
    <div data-testid="compare-options-modal"
         className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-card border border-border p-6 max-w-4xl w-full space-y-4"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">Compare Design Options</div>
          <button type="button" data-testid="btn-close-compare" onClick={onClose}
                  className="btn-outline text-[11px] min-h-[32px] px-3">Close</button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <CompareSide side="A" testid="compare-side-a" options={options} selectedId={aId} onSelect={setAId} option={a}/>
          <CompareSide side="B" testid="compare-side-b" options={options} selectedId={bId} onSelect={setBId} option={b}/>
        </div>
        <div className="text-[10px] text-muted-foreground">
          Future: 2D · 3D · AI Render tabs per side. Investment column will appear once Phase 1B pricing is per-option.
        </div>
      </div>
    </div>
  );
}

function CompareSide({ side, testid, options, selectedId, onSelect, option }) {
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <div className="overline text-[10px]">Option {side}</div>
        <select data-testid={`${testid}-select`}
                className="luxe-input text-[11px]"
                value={selectedId}
                onChange={(e) => onSelect(e.target.value)}>
          <option value="">— Select —</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>V{o.version_number} · {o.name} · {o.status}</option>
          ))}
        </select>
      </div>
      {option ? (
        <div className="space-y-2">
          <div className="border border-border bg-white">
            {option.thumbnail_svg
              ? <div dangerouslySetInnerHTML={{ __html: option.thumbnail_svg }} style={{ width: "100%" }}/>
              : <div className="h-40 bg-muted"/>}
          </div>
          <div className="text-[11px] font-medium">{option.name}</div>
          <div className="text-[10px] text-muted-foreground">
            V{option.version_number} · {option.status} · {(option.created_at || "").slice(0, 10)}
          </div>
          <div className="text-[10px] text-muted-foreground">Saved: {(option.created_at || "").slice(0, 10)}</div>
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground py-6 text-center">Pick a Design Option.</div>
      )}
    </div>
  );
}
